import { useEffect, useState } from "react";
import api, { formatApiError, downloadFile, previewFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, validityBadge } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, Eye, Download, Truck, CreditCard } from "lucide-react";
import { toast } from "sonner";

export default function CustomerDrivers() {
  const { user } = useAuth();
  const firmId = user.firm_id;
  const [src5, setSrc5] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    if (!firmId) { setAllowed(false); return; }
    api.get(`/firms/${firmId}`).then(({ data }) => {
      const ok = (data.activities || []).includes("tasiyan");
      setAllowed(ok);
      if (!ok) return;
      Promise.all([
        api.get(`/firms/${firmId}/src5-records`).catch(() => ({ data: [] })),
        api.get(`/firms/${firmId}/vehicles`).catch(() => ({ data: [] })),
      ]).then(([s, v]) => { setSrc5(s.data); setVehicles(v.data); });
    }).catch((e) => { setAllowed(false); toast.error(formatApiError(e.response?.data?.detail)); });
  }, [firmId]);

  if (!firmId) return <p className="text-sm text-slate-500">Firma bağlantınız bulunamadı.</p>;
  if (allowed === null) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>;
  if (!allowed) return <p className="text-sm text-slate-500" data-testid="cdrivers-not-allowed">Bu bölüm yalnızca taşıyan faaliyetine sahip firmalar içindir.</p>;

  const DocBtns = ({ pdf, name }) => pdf ? (
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="sm" onClick={() => previewFile(`/documents/${pdf.id}/download`)} data-testid={`cdrivers-preview-${name}`}><Eye className="h-4 w-4 mr-1" /> Önizle</Button>
      <Button variant="ghost" size="sm" onClick={() => downloadFile(`/documents/${pdf.id}/download`, `${pdf.name}.pdf`)}><Download className="h-4 w-4 mr-1" /> İndir</Button>
    </div>
  ) : <span className="text-xs text-slate-400">Belge yok</span>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Sürücü ve Araç Belgeleri</h1>
        <p className="text-sm text-slate-500 mt-1">SRC5 ve araç belgelerinizin geçerlilik durumları ve yayımlanmış belgeler.</p>
      </div>

      <div>
        <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2 mb-2"><CreditCard className="h-5 w-5 text-sky-500" /> SRC5 Belgeleri</h3>
        {src5 === null ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-sky-600" /></div> : (
          src5.length === 0 ? <p className="text-sm text-slate-400">SRC5 kaydı yok.</p> : (
            <div className="space-y-2">{src5.map((s) => {
              const b = validityBadge(s.valid_until);
              return (
                <Card key={s.personnel_id} className="border-slate-200" data-testid={`cdrivers-src5-${s.personnel_id}`}>
                  <CardContent className="py-3 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="font-medium text-sm text-slate-800">{s.driver_name}</div>
                      <div className="text-xs text-slate-500">SRC5 No: {s.number || "—"} · Geçerlilik: {s.valid_until ? formatDate(s.valid_until) : "Tarih girilmedi"}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <StatusBadge tone={b.tone}>{b.text}</StatusBadge>
                      <DocBtns pdf={s.pdf} name={`src5-${s.personnel_id}`} />
                    </div>
                  </CardContent>
                </Card>
              );
            })}</div>
          )
        )}
      </div>

      <div>
        <h3 className="font-display font-semibold text-slate-800 flex items-center gap-2 mb-2"><Truck className="h-5 w-5 text-sky-500" /> Araç / ADR Belgeleri</h3>
        {vehicles.length === 0 ? <p className="text-sm text-slate-400">Araç kaydı yok.</p> : (
          <div className="space-y-2">{vehicles.map((v) => {
            const b = validityBadge(v.valid_until);
            return (
              <Card key={v.id} className="border-slate-200" data-testid={`cdrivers-vehicle-${v.id}`}>
                <CardContent className="py-3 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-medium text-sm text-slate-800">{v.plate}</div>
                    <div className="text-xs text-slate-500">{v.doc_title} · Geçerlilik: {v.valid_until ? formatDate(v.valid_until) : "Tarih girilmedi"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge tone={b.tone}>{b.text}</StatusBadge>
                    <DocBtns pdf={v.pdf} name={`veh-${v.id}`} />
                  </div>
                </CardContent>
              </Card>
            );
          })}</div>
        )}
      </div>
    </div>
  );
}
